<template>
  <div class="selection-archive-list" data-testid="selection-archive-list">
    <div v-if="archives.length === 0" class="small text-muted" data-testid="selection-archive-none">
      {{ noneText }}
    </div>
    <div
      v-for="archive in archives"
      :key="archive.id"
      class="selection-archive-row border rounded p-2 mb-2"
      data-testid="selection-archive-row"
      :data-status="archive.status"
    >
      <div class="d-flex flex-wrap align-items-center">
        <span class="font-weight-bold text-break" data-testid="selection-archive-row-name">
          <i v-if="archive.kind === 'folder' || archive.label_kind === 'folder'" class="fas fa-folder mr-1"></i>{{ archive.name }}
        </span>
        <span class="small text-muted ml-auto">{{ countSizeText(archive) }}</span>
      </div>
      <div v-if="archive.created_at" class="small text-muted" data-testid="selection-archive-row-created">
        {{ createdAtText(archive) }}
      </div>
      <div v-if="archive.expires_at" class="small text-muted" data-testid="selection-archive-row-expires">
        {{ expiresAtText(archive) }}
      </div>

      <div v-if="isPreparing(archive)" class="small mt-1" data-testid="selection-archive-row-preparing">
        <i class="fas fa-spinner fa-spin mr-1"></i>{{ creatingLabel }}
      </div>
      <div
        v-else-if="archive.status === 'FAILED' && archive.error_message"
        class="text-danger small mt-1"
        data-testid="selection-archive-row-failed"
      >
        {{ archive.error_message }}
      </div>
      <fast-dl
        v-else-if="archive.status === 'DONE'"
        class="mt-1 w-100"
        data-testid="selection-archive-row-download"
        :video-id="archive.uuid"
        :signed-url-endpoint="downloadUrlsEndpoint(archive)"
        :known-total-bytes="Number(archive.zip_size || archive.total_size || 0)"
        range-supported-hint="bytes"
        :chunk-mb="64"
        :initial-concurrency="12"
        :max-concurrency="20"
        :ordered-write="true"
        :start-label="downloadLabel"
      >
        <template #default>
          <i class="fas fa-download mr-1"></i>{{ downloadLabel }}
        </template>
      </fast-dl>

    </div>

    <div v-if="pollTimeoutReached" class="small text-muted" data-testid="selection-archive-poll-timeout">
      {{ preparingTimeoutText }}
    </div>
  </div>
</template>

<script setup>
import _ from 'lodash';

defineProps({
  archives: { type: Array, default: () => [] },
  pollTimeoutReached: { type: Boolean, default: false },
});

const translate = (key, fallback = '') => _.get(window.i18n, key) || fallback || key;

const noneText = translate('folder.selection_archive_none', '作成したZIPはありません。');
const creatingLabel = translate('button.folder_archive_creating', 'ZIP準備中');
const downloadLabel = translate('button.folder_archive_download', 'ZIPをダウンロード');
const preparingTimeoutText = translate(
  'folder.download_preparing_timeout',
  '時間がかかっています。準備ができるとメールが届きます。'
);

const isPreparing = (archive) => archive.status === 'PENDING' || archive.status === 'RUNNING';

// created_at / expires_at はサーバー(Asia/Tokyo)のオフセット付きISO8601。
// dayjs等で整形すると閲覧者のブラウザのタイムゾーンに変換されるため、FolderArchivePanel.vue と同じく文字列から直接取り出す。
const toDatetimeText = (value) => String(value).slice(0, 16).replace('T', ' ');

const createdAtText = (archive) =>
  translate('folder.download_created_at', '作成日時: :datetime').replace(':datetime', toDatetimeText(archive.created_at));
const expiresAtText = (archive) =>
  translate('folder.selection_archive_expires_at', 'ダウンロード期限: :datetime').replace(
    ':datetime',
    toDatetimeText(archive.expires_at)
  );
const countSizeText = (archive) =>
  translate('folder.selection_archive_count_size', ':count件・:size')
    .replace(':count', String(archive.file_count ?? 0))
    .replace(':size', String(archive.total_size_text ?? ''));

const downloadUrlsEndpoint = (archive) => `/api/folders/archives/${archive.id}/download-urls`;
</script>
